import React, { useState, useEffect } from 'react';
import { useSync } from '../../contexts/SyncContext';
import { useOffline } from '../../contexts/OfflineContext';
import { getSyncAdapter } from '../../libs/syncClient';
import { esRechazadoPorPermisos } from '../../libs/syncErrors';

interface SyncStatusPanelProps {
  className?: string;
  setShowQueueViewer: (show: boolean) => void;
}

const SyncStatusPanel: React.FC<SyncStatusPanelProps> = ({ className = '',  setShowQueueViewer}) => {
  const { sync, ready, isOnline } = useSync();
  const { retryWithBackoff } = useOffline();
  const [lastSyncTime, setLastSyncTime] = useState<string>('Nunca');
  const [queueCount, setQueueCount] = useState<number>(0);
  const [rechazadosCount, setRechazadosCount] = useState<number>(0);
  const [isSyncing, setIsSyncing] = useState<boolean>(false);

  useEffect(() => {
    const updateSyncInfo = async () => {
      if (sync && ready) {
        try {
          const lastSync = await sync.getLastSyncTime();
          const formattedTime = lastSync !== '1970-01-01T00:00:00.000Z'
            ? new Date(lastSync).toLocaleString('es-AR')
            : 'Nunca';
          setLastSyncTime(formattedTime);

          // Get pending changes count
          const pendingCount = await sync.getPendingCount();
          setQueueCount(pendingCount);

          // Cambios rechazados por permisos (RLS): se retienen sin reintentar
          // y hay que avisarle al usuario — no quedan silenciados en la cola.
          const adapter = getSyncAdapter();
          if (adapter?.getPendingChanges) {
            const changes = await adapter.getPendingChanges();
            setRechazadosCount(changes.filter(c => esRechazadoPorPermisos(c.lastError)).length);
          }
        } catch (error) {
          console.error('Error getting sync info:', error);
        }
      }
    };

    updateSyncInfo();

    // El SyncManager avisa cuando un push es rechazado por permisos: refrescar
    // al instante en vez de esperar el intervalo de 30s.
    const onRechazado = () => updateSyncInfo();
    window.addEventListener('mcd:sync-rechazado', onRechazado);

    // Update periodically
    const interval = setInterval(updateSyncInfo, 30000); // Every 30 seconds
    return () => {
      clearInterval(interval);
      window.removeEventListener('mcd:sync-rechazado', onRechazado);
    };
  }, [sync, ready]);

  const handleManualSync = async () => {
    if (!sync || !ready) return;

    setIsSyncing(true);
    try {
      await retryWithBackoff(async () => {
        await sync.syncPending();
        // Update info after sync
        const lastSync = await sync.getLastSyncTime();
        setLastSyncTime(lastSync !== '1970-01-01T00:00:00.000Z'
          ? new Date(lastSync).toLocaleString('es-AR')
          : 'Nunca');
        const pendingCount = await sync.getPendingCount();
        setQueueCount(pendingCount);
      });
    } catch (error) {
      console.error('Manual sync failed:', error);
    } finally {
      setIsSyncing(false);
    }
  };

  const getStatusColor = () => {
    if (!ready) return 'text-sage';
    if (rechazadosCount > 0) return 'text-danger';
    if (isSyncing) return 'text-warning';
    if (!isOnline) return 'text-danger';
    return queueCount > 0 ? 'text-warning' : 'text-success';
  };

  const getStatusText = () => {
    if (!ready) return 'No inicializado';
    if (rechazadosCount > 0) return `${rechazadosCount} rechazado${rechazadosCount === 1 ? '' : 's'}`;
    if (isSyncing) return 'Sincronizando...';
    if (!isOnline) return 'Sin conexión';
    return queueCount > 0 ? 'Pendiente' : 'Sincronizado';
  };

  return (
    <div className={`flex items-center space-x-4 px-3 py-2 rounded-lg bg-white border border-hairline shadow-card ${className}`}>
      <div className="flex flex-col">
        <div className="flex items-center space-x-2">
          <div className={`w-3 h-3 rounded-pixel ${
            !ready ? 'bg-sage' :
            rechazadosCount > 0 ? 'bg-danger' :
            isSyncing ? 'bg-warning animate-pulse' :
            !isOnline ? 'bg-danger' :
            queueCount > 0 ? 'bg-warning' : 'bg-success'
          }}`}></div>
          <span className={`text-sm font-medium ${getStatusColor()}`}>
            {getStatusText()}
          </span>
        </div>
        {rechazadosCount > 0 ? (
          <button
            onClick={() => setShowQueueViewer(true)}
            className="text-left text-xs text-danger mt-1 hover:underline"
            title="Cambios que el servidor rechazó por permisos. Se guardaron localmente pero no se van a sincronizar: revisá la Cola."
          >
            ⚠ {rechazadosCount} cambio{rechazadosCount === 1 ? '' : 's'} rechazado{rechazadosCount === 1 ? '' : 's'} por permisos — ver Cola
          </button>
        ) : (
          <div className="text-xs text-sage mt-1">
            Cola: {queueCount} | Última sync: {lastSyncTime}
          </div>
        )}
      </div>

      {/* Queue Viewer Button */}
      <button
        onClick={() => setShowQueueViewer(true)}
        className={`px-2 py-1 sm:px-3 text-xs font-medium border rounded-md transition-colors ${
          rechazadosCount > 0
            ? 'text-danger bg-danger/10 border-danger/30 hover:bg-danger/20'
            : 'text-ink bg-paper-2 border-hairline hover:bg-hairline/50'
        }`}
      >
        Cola{rechazadosCount > 0 ? ` (${rechazadosCount})` : ''}
      </button>

      {ready && (
        <button
          onClick={handleManualSync}
          disabled={isSyncing || !isOnline}
          className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
            isSyncing || !isOnline
              ? 'bg-paper-2 text-sage cursor-not-allowed'
              : queueCount > 0
                ? 'bg-warning text-paper hover:brightness-110'
                : 'bg-forest text-paper hover:bg-forest-mid'
          }`}
        >
          {isSyncing ? '...' : 'Sync'}
        </button>
      )}
    </div>
  );
};

export default SyncStatusPanel;